import { clone, debounce, set } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button, FormGroup, Input, Label } from "../components/Modal";
import axios from "../general/HttpClient";
import QRCodeView from "../general/QRCode";
import * as AccountStore from "./AccountStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Avatar from "react-avatar";

const { Component } = React;

interface ProfileMultiFactorState {
    currentUser: any;
    showResendConfirmationModal: boolean;
    userUpdated: boolean;
    user: any;
    showUserUpdatedMessage: boolean;
    sharedKey: string;
    authenticatorUri: string;
}

export default class ProfileMultiFactor extends Component<any, ProfileMultiFactorState> {
    constructor(props) {
        super(props);
        this.state = {
            currentUser: {},
            showResendConfirmationModal: false,
            userUpdated: false,
            user: { twoFactorEnabled: false },
            showUserUpdatedMessage: false,
            sharedKey: "",
            authenticatorUri: "",
        };
    }

    componentDidMount() {
        this.debouncedUpdate = debounce(() => {
            return AccountStore.UpdateUser(this.state.user).then((response) => {
                this.GetCurrentUser();
                this.setState({ showUserUpdatedMessage: true, userUpdated: true });
                setTimeout(() => {
                    this.setState({ showUserUpdatedMessage: false });
                }, 1000);
                return response;
            });
        }, 300);
        this.GetCurrentUser();
    }

    debouncedUpdate = () => {
        return AccountStore.UpdateUser(this.state.user).then((response) => {
            this.GetCurrentUser();
            this.setState({ showUserUpdatedMessage: true, userUpdated: true });
            setTimeout(() => {
                this.setState({ showUserUpdatedMessage: false });
            }, 1000);
            return response;
        });
    };

    GetCurrentUser = () => {
        AccountStore.GetCurrentLoggedInUser(true).then((user) => {
            this.setState({
                currentUser: user,
                user: clone(user),
            });
        });
    };

    handleProfileDataChange = (key) => (e) => {
        let value = e.target.value;
        this.setState((prevState) => {
            set(prevState.user, key, value);
            return { user: prevState.user, userUpdated: false };
        }, this.debouncedUpdate);
    };

    getAuthenticatorDetails = () => {
        axios.get("/api/AccountActions/GetAuthenticatorDetails").then((response) => {
            var { sharedKey, authenticatorUri } = response.data;
            this.setState({ sharedKey, authenticatorUri });
        });
    };

    resetAuthenticatorDetails = () => {
        this.setState({ sharedKey: "", authenticatorUri: "" });
        axios.post("/api/AccountActions/ResetAuthenticatorDetails").then((response) => {
            var { sharedKey, authenticatorUri } = response.data;
            this.setState({ sharedKey, authenticatorUri });
        });
    };

    updateUser = () => {
        AccountStore.UpdateUser(this.state.user).then((response) => {
            this.props.history.push("/");
        });
    };

    handleTwoFactorChange = (e) => {
        let value = e.target.checked;

        this.setState((prevState) => {
            prevState.user.twoFactorEnabled = value;
            return { user: prevState.user, userUpdated: false };
        }, this.debouncedUpdate);
    };

    render() {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-4">
                        <Avatar round={true} size="70" name={this.state.currentUser.fullName} src={this.state.currentUser.photo} />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                {this.state.currentUser.firstName} {this.state.currentUser.lastName}
                            </h1>
                            <h2 className="text-xl text-gray-600">{this.state.currentUser.email}</h2>
                            <p className="text-sm text-gray-500">{this.state.currentUser.userName}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <nav className="flex space-x-4 px-6 py-3 border-b border-gray-200">
                        <Link to="/profile" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "user"]} className="mr-2" />
                            Profile
                        </Link>
                        <Link to="/profile/data" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "database"]} className="mr-2" />
                            Data
                        </Link>
                        <Link to="/profile/mfa" className="px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 border-b-2 border-blue-600">
                            <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2" />
                            MFA
                        </Link>
                        <Link to="/profile/security" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "lock"]} className="mr-2" />
                            Security
                        </Link>
                        <Link to="/profile/notifications" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2" />
                            Notifications
                        </Link>
                    </nav>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2 text-purple-500" />
                        Two-Factor Authentication
                    </h3>

                    <div className="max-w-2xl space-y-6">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <FontAwesomeIcon icon={["fad", "mobile"]} className="text-2xl text-blue-600" />
                                <div>
                                    <p className="font-medium text-gray-900">Require Two-Factor Authentication</p>
                                    <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={this.state.user.twoFactorEnabled} onChange={this.handleTwoFactorChange} className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        {this.state.user.twoFactorEnabled && (
                            <div className="space-y-4">
                                {!this.state.authenticatorUri && (
                                    <div className="text-center py-8">
                                        <FontAwesomeIcon icon={["fad", "qrcode"]} className="text-5xl text-gray-300 mb-4" />
                                        <p className="text-gray-600 mb-4">Set up your authenticator app</p>
                                        <Button variant="primary" onClick={this.getAuthenticatorDetails}>
                                            <FontAwesomeIcon icon={["fad", "key"]} className="mr-2" />
                                            Get Authenticator Code
                                        </Button>
                                    </div>
                                )}
                                {this.state.authenticatorUri && (
                                    <div className="space-y-4">
                                        <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 flex justify-center">
                                            <QRCodeView text={this.state.authenticatorUri}></QRCodeView>
                                        </div>

                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                            <p className="text-sm font-medium text-blue-900 mb-2">Manual Entry Key:</p>
                                            <code className="text-xs text-blue-700 break-all block mb-2">{this.state.sharedKey}</code>
                                            <p className="text-xs text-blue-600">Scan the QR code or enter this key manually in your authenticator app</p>
                                        </div>

                                        <Button variant="outline-danger" onClick={this.resetAuthenticatorDetails}>
                                            <FontAwesomeIcon icon={["fad", "rotate"]} className="mr-2" />
                                            Reset Authenticator Code
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {this.state.showUserUpdatedMessage && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-500 mr-2" />
                                    <p className="text-sm font-medium text-green-800">Profile updated successfully</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
